import {
  EMPTY_PROGRESS,
  type LevelProgress,
  mergeProgressStates,
  type ProgressState,
} from '@md/shared';

/**
 * Store de progression du joueur (concept §4.3) : étoiles gagnées par niveau.
 *
 * Depuis la v1.3.0, le compte joueur est OBLIGATOIRE et la progression vit en
 * base côté serveur : ce store n'est plus qu'un miroir EN MÉMOIRE de la
 * session courante — hydraté par `absorb()` à la connexion, poussé au serveur
 * à chaque victoire. Aucun `localStorage` : une seule source de vérité.
 */

/** Résultat d'une partie terminée, tel que produit par le moteur. */
export interface LevelOutcome {
  readonly levelId: string;
  readonly stars: number;
  readonly timeMs: number;
  readonly bolts: number;
  readonly deaths: number;
}

export interface ProgressStore {
  /** État courant (jamais `null`). */
  state(): ProgressState;
  /** Progression d'un niveau, `undefined` s'il n'a jamais été terminé. */
  level(levelId: string): LevelProgress | undefined;
  /**
   * Enregistre une victoire : ne conserve QUE les améliorations (étoiles
   * max, meilleur temps, meilleur nombre de boulons). Renvoie le nouvel état.
   */
  record(outcome: LevelOutcome): ProgressState;
  /** Total d'étoiles gagnées, tous niveaux confondus. */
  totalStars(): number;
  /**
   * Fusionne une progression venue du serveur (compte) : uniquement vers le
   * haut, jamais de perte — voir `mergeProgressStates`.
   */
  absorb(incoming: ProgressState): ProgressState;
  /** Efface la progression en mémoire (déconnexion, outil de test). */
  reset(): void;
  /** Abonnement aux changements ; renvoie la fonction de désabonnement. */
  subscribe(listener: (state: ProgressState) => void): () => void;
}

export interface ProgressStoreOptions {
  /** Horloge injectable (tests). */
  readonly now?: () => number;
}

/**
 * Le run à retenir pour `deaths` est celui qui a rapporté le plus d'étoiles,
 * et à égalité le plus rapide — cohérent avec la façon dont `stars` et
 * `bestTimeMs` sont eux-mêmes mis à jour.
 */
function outcomeIsBestRun(previous: LevelProgress, outcome: LevelOutcome): boolean {
  if (outcome.stars !== previous.stars) {
    return outcome.stars > previous.stars;
  }
  return previous.bestTimeMs === null || outcome.timeMs < previous.bestTimeMs;
}

function mergeOutcome(
  previous: LevelProgress | undefined,
  outcome: LevelOutcome,
  completedAt: number,
): LevelProgress {
  if (!previous) {
    return {
      stars: outcome.stars,
      bestTimeMs: outcome.timeMs,
      bestBolts: outcome.bolts,
      deaths: outcome.deaths,
      completedAt,
    };
  }
  return {
    stars: Math.max(previous.stars, outcome.stars),
    bestTimeMs:
      previous.bestTimeMs === null ? outcome.timeMs : Math.min(previous.bestTimeMs, outcome.timeMs),
    bestBolts: Math.max(previous.bestBolts, outcome.bolts),
    deaths: outcomeIsBestRun(previous, outcome) ? outcome.deaths : previous.deaths,
    // Le premier succès seulement : jamais réécrit par les runs suivants.
    completedAt: previous.completedAt,
  };
}

export function createProgressStore(options: ProgressStoreOptions = {}): ProgressStore {
  const now = options.now ?? (() => Date.now());
  const listeners = new Set<(state: ProgressState) => void>();

  let state: ProgressState = EMPTY_PROGRESS;

  function notify(): void {
    for (const listener of listeners) {
      listener(state);
    }
  }

  const store: ProgressStore = {
    state(): ProgressState {
      return state;
    },

    level(levelId: string): LevelProgress | undefined {
      return state.levels[levelId];
    },

    record(outcome: LevelOutcome): ProgressState {
      const merged = mergeOutcome(state.levels[outcome.levelId], outcome, now());
      state = { ...state, levels: { ...state.levels, [outcome.levelId]: merged } };
      notify();
      return state;
    },

    totalStars(): number {
      let total = 0;
      for (const level of Object.values(state.levels)) {
        total += level.stars;
      }
      return total;
    },

    absorb(incoming: ProgressState): ProgressState {
      state = mergeProgressStates(state, incoming);
      notify();
      return state;
    },

    reset(): void {
      state = EMPTY_PROGRESS;
      notify();
    },

    subscribe(listener: (state: ProgressState) => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };

  return store;
}
